'use client'

import React, { useState, useEffect } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import type { Variants } from 'framer-motion'
import { FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi'
import { useLanguage } from '../../providers/LanguageProvider'

type Point = { label: string; labelAr: string; gallery: string[] }

const containerVariants: Variants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.2,
    },
  },
}

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] },
  },
}

const galleryCardVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 1) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.4,
      ease: [0.25, 0.1, 0.25, 1],
    },
  }),
}

const MotionImage = motion(Image)

export default function Gallery() {
  const { language } = useLanguage()
  const isArabic = language === 'ar'

  const [selected, setSelected] = useState<string | null>(null)
  const [showPoints, setShowPoints] = useState(false)
  const [pointsComplete, setPointsComplete] = useState(false)
  const [selectedPoint, setSelectedPoint] = useState<Point | null>(null)
  const [galleryPage, setGalleryPage] = useState(0)
  // New state to track which image's points we're showing
  const [selectedImageForPoints, setSelectedImageForPoints] = useState<string | null>(null)
  // New state to track navigation history for back functionality
  const [pointsHistory, setPointsHistory] = useState<'main' | Point>('main')

  type GalleryImage = {
    src: string
    alt: string
    altAr: string
    points: {
      label: string
      labelAr: string
      gallery: string[]
    }[]
  }

  const [imageData, setImageData] = useState<GalleryImage[]>([])

  useEffect(() => {
    const fetchGallery = async () => {
      const res = await fetch('/api/fetch-gallery')
      const data = await res.json()
      setImageData(data.reverse())
    }

    fetchGallery()
  }, [])

  useEffect(() => {
    if (selected) {
      const timer = setTimeout(() => setShowPoints(true), 400)
      setPointsComplete(false)
      return () => clearTimeout(timer)
    } else {
      setShowPoints(false)
    }
  }, [selected])

  const selectedImage = imageData.find((img) => img.src === selected)
  // Get the image data for points view
  const imageForPoints = imageData.find((img) => img.src === selectedImageForPoints)
  const [openedImage, setOpenedImage] = useState('')

  // Handle point click - navigate to point's gallery
  const handlePointClick = (point: Point) => {
    setPointsHistory('main') // Remember we came from main points
    setSelectedPoint(point)
  }

  // Handle back from gallery to points
  const handleBackToPoints = () => {
    setSelectedPoint(null)
    setPointsHistory('main')
    setGalleryPage(0)
  }

  // Handle close points completely
  const handleClosePoints = () => {
    setSelectedImageForPoints(null)
    setSelectedPoint(null)
    setPointsHistory('main')
    setGalleryPage(0)
  }

  // Translation texts
  const translations = {
    en: {
      home: 'Home',
      gallery: 'Gallery',
      title: 'Our Facility in Action',
      previous: 'Previous',
      next: 'Next',
    },
    ar: {
      home: 'الرئيسية',
      gallery: 'المعرض',
      title: 'منشأتنا قيد العمل',
      previous: 'السابق',
      next: 'التالي',
    },
  }

  const t = translations[isArabic ? 'ar' : 'en']

  if (imageData.length === 0)
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    )

  return (
    <div className={`block md:hidden min-h-screen bg-white pt-12 pb-28 px-4 ${isArabic ? 'rtl' : 'ltr'}`}>
      <div className="max-w-sm mx-auto">
        <h1 className="text-xl font-normal text-[#414141] mt-8 mb-6">{t.title}</h1>

        <div className="relative">
          {/* Section 1: Mobile Image Grid */}
          <div className="grid grid-cols-1 gap-4 mt-4">
            {imageData.map((img, index) => (
              <div key={img.src}>
                <motion.div
                  layoutId={img.src}
                  className="relative group overflow-hidden h-48 cursor-pointer "
                  onClick={() => {
                    // Toggle points for this image
                    if (selectedImageForPoints === img.src && !selectedPoint) {
                      handleClosePoints()
                    } else {
                      setSelectedImageForPoints(img.src)
                      setSelectedPoint(null)
                      setPointsHistory('main')
                      setSelected(null)
                    }
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.4 }}
                >
                  <Image   
                    src={img.src}
                    alt={isArabic ? img.altAr : img.alt}
                    fill
                    className="object-cover "
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent " />
                  <div
                    className={`absolute bottom-4 text-white flex items-center gap-2 ${isArabic ? 'right-4' : 'left-4'}`}
                  >
                    <h3 className="text-sm font-medium">{isArabic ? img.altAr : img.alt}</h3>
                    <FiChevronRight
                      className={`text-white text-lg transition-transform ${
                        selectedImageForPoints === img.src && !selectedPoint && isArabic
                          ? 'rotate-90'
                          : selectedImageForPoints === img.src && !selectedPoint && !isArabic
                            ? 'rotate-90'
                            : isArabic
                              ? 'rotate-180'
                              : ''
                      }`}
                    />
                  </div>
                </motion.div>

                {/* Points Section - Shows directly under the clicked image */}
                <AnimatePresence>
                  {selectedImageForPoints === img.src && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="bg-gray-900 rounded-lg mt-2 overflow-hidden"
                    >
                      <div className="p-4">
                        {/* Header with back button when viewing gallery */}
                        {selectedPoint && (
                          <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-700">
                            <button
                              onClick={handleBackToPoints}
                              className="text-white text-lg p-1 hover:bg-gray-800 rounded"
                            >
                              <FiChevronLeft className={isArabic ? 'rotate-180' : ''} />
                            </button>
                            <h3 className="text-white text-base font-medium text-center flex-1 mx-4 capitalize">
                              {isArabic ? selectedPoint.labelAr : selectedPoint.label}
                            </h3>
                            <button
                              onClick={handleClosePoints}
                              className="text-white text-lg p-1 hover:bg-gray-800 rounded"
                            >
                              <FiX />
                            </button>
                          </div>
                        )}

                        {/* Content Area */}
                        <AnimatePresence mode="wait">
                          {!selectedPoint ? (
                            // Show main points list
                            <motion.div
                              key="main-points"
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: -20 }}
                              transition={{ duration: 0.3 }}
                            >
                              <motion.div
                                variants={containerVariants}
                                initial="hidden"
                                animate="show"
                                className="space-y-3"
                              >
                                {img.points.map((point, pointIndex) => (
                                  <motion.div
                                    key={pointIndex}
                                    variants={itemVariants}
                                    className="border-b border-gray-700 last:border-b-0 pb-3 last:pb-0"
                                  >
                                    <button
                                      onClick={() => handlePointClick(point)}
                                      className="w-full flex items-center justify-between text-white text-[14px] py-2 hover:bg-gray-800 rounded px-2 transition-colors capitalize"
                                    >
                                      <span className={isArabic ? 'text-right' : 'text-left'}>
                                        {isArabic ? point.labelAr : point.label}
                                      </span>
                                      <FiChevronRight
                                        className={`text-white text-sm ${isArabic ? 'rotate-180' : ''}`}
                                      />
                                    </button>
                                  </motion.div>
                                ))}
                              </motion.div>
                            </motion.div>
                          ) : (
                            // Show selected point's gallery
                            <motion.div
                              key="point-gallery"
                              initial={{ opacity: 0, x: 20 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: 20 }}
                              transition={{ duration: 0.3 }}
                            >
                              {/* Gallery Grid - Mobile: 1 column, show 3 per page to fit better */}
                              <div className="grid grid-cols-1 gap-3 mb-4">
                                {selectedPoint.gallery
                                  .slice(galleryPage * 3, (galleryPage + 1) * 3)
                                  .map((src, index) => (
                                    <motion.div
                                      key={index}
                                      className="relative h-40 overflow-hidden"
                                      custom={index}
                                      initial="hidden"
                                      animate="show"
                                      variants={galleryCardVariants}
                                    >
                                      <MotionImage
                                        src={src}
                                        alt={`${isArabic ? selectedPoint.labelAr : selectedPoint.label} ${index + 1}`}
                                        fill
                                        className="object-cover"
                                        onClick={() => setOpenedImage(src)}
                                        initial={{ scale: 1 }}
                                        whileHover={{ scale: 1.05 }}
                                        transition={{ duration: 0.3 }}
                                      />
                                    </motion.div>
                                  ))}
                              </div>
                              <AnimatePresence>
                                {openedImage && (
                                  <motion.div
                                    className="fixed inset-0 bg-black/90 backdrop-blur-md flex justify-center items-center z-[999]"
                                    onClick={() => setOpenedImage('')}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                  >
                                    <div
                                      className="relative w-[96%] h-[100%] p-4"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <button
                                        className="absolute top-3 right-3 text-white text-xl font-bold z-10"
                                        onClick={() => setOpenedImage('')}
                                      >
                                        ✕
                                      </button>
                                      <Image   
                                        src={openedImage}
                                        alt="Selected"
                                        fill
                                        className="w-full h-auto object-contain"
                                      />
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>

                              {/* Navigation Controls */}
                              {selectedPoint.gallery.length > 3 && (
                                <>
                                  <div className="flex items-center justify-between mb-3">
                                    <button
                                      onClick={() =>
                                        setGalleryPage((prev) => Math.max(prev - 1, 0))
                                      }
                                      disabled={galleryPage === 0}
                                      className="flex items-center text-white disabled:opacity-30 text-sm py-1 px-2 hover:bg-gray-800 transition-colors"
                                    >
                                      <FiChevronLeft
                                        className={`text-sm ${isArabic ? 'rotate-180' : ''}`}
                                      />
                                      <span className="ml-1">{t.previous}</span>
                                    </button>

                                    <button
                                      onClick={() =>
                                        setGalleryPage((prev) =>
                                          selectedPoint
                                            ? Math.min(
                                                prev + 1,
                                                Math.floor(selectedPoint.gallery.length / 3),
                                              )
                                            : prev,
                                        )
                                      }
                                      disabled={
                                        !selectedPoint ||
                                        galleryPage >= Math.floor(selectedPoint.gallery.length / 3)
                                      }
                                      className="flex items-center text-white disabled:opacity-30 text-sm py-1 px-2 hover:bg-gray-800  transition-colors"
                                    >
                                      <span className="mr-1">{t.next}</span>
                                      <FiChevronRight
                                        className={`text-sm ${isArabic ? 'rotate-180' : ''}`}
                                      />
                                    </button>
                                  </div>

                                  {/* Pagination Dots */}
                                  <div className="flex justify-center space-x-1 rtl:space-x-reverse mb-5 md:mb-3">
                                    {Array.from({
                                      length: selectedPoint
                                        ? Math.ceil(selectedPoint.gallery.length / 3)
                                        : 0,
                                    }).map((_, i) => (
                                      <button
                                        key={i}
                                        onClick={() => setGalleryPage(i)}
                                        className={`w-2 h-2 rounded-full transition-colors ${
                                          i === galleryPage ? 'bg-white' : 'bg-gray-500'
                                        }`}
                                      />
                                    ))}
                                  </div>
                                </>
                              )}

                              {/* Footer Info */}
                              <div className="md:text-center text-gray-400 text-xs">
                                <div className="mb-1">{isArabic ? img.altAr : img.alt}</div>
                                <div className="text-white text-xs capitalize">
                                  {isArabic ? selectedPoint.labelAr : selectedPoint.label}
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* Section 2 (Original): Mobile Overlay (Expanded View + Points) - Keep for other functionality if needed */}
          <AnimatePresence>
            {selected && !selectedPoint && (
              <motion.div
                key="expanded"
                layoutId={selected}
                className="fixed inset-0 z-50 bg-black"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                dir={isArabic ? 'rtl' : 'ltr'}
              >
                {/* Close Button */}
                <button
                  onClick={() => setSelected(null)}
                  className="absolute top-6 end-4 z-30 text-white text-2xl p-2"
                >
                  <FiX />
                </button>

                {/* Fullscreen Background Image */}
                <div className="relative h-1/2">
                  <Image    src={selected} alt="Expanded" fill className="object-cover" />

                  {/* Alt Title on Image */}
                  <div className="absolute bottom-4 start-4 text-white text-sm font-medium">
                    {isArabic ? selectedImage?.altAr : selectedImage?.alt}
                  </div>
                </div>

                {/* Points Section */}
                <div className="h-1/2 overflow-y-auto bg-black p-4">
                  <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="show"
                    className="space-y-4"
                  >
                    {selectedImage?.points.map((point, index) => (
                      <motion.div
                        key={index}
                        variants={itemVariants}
                        className="border-b border-gray-700 pb-3"
                        onAnimationComplete={() => {
                          if (index === selectedImage.points.length - 1) {
                            setPointsComplete(true)
                          }
                        }}
                      >
                        <button
                          onClick={() => setSelectedPoint(point)}
                          className="w-full flex items-center justify-between text-white text-base py-2"
                        >
                          <span className={isArabic ? 'text-right' : 'text-left'}>
                            {isArabic ? point.labelAr : point.label}
                          </span>
                          {pointsComplete && (
                            <motion.span
                              initial={{ opacity: 0, x: isArabic ? -10 : 10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ duration: 0.3, delay: index * 0.1 }}
                              className="text-white text-lg"
                            >
                              {isArabic ? <FiChevronLeft /> : <FiChevronRight />}
                            </motion.span>
                          )}
                        </button>
                      </motion.div>
                    ))}
                  </motion.div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Section 3: Mobile Gallery for Selected Point (Full Screen) - Keep this for when accessed from Section 2 */}
          <AnimatePresence>
            {selectedPoint && selected && (
              <motion.div
                key="gallery"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black"
              >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-800">
                  <button onClick={() => setSelectedPoint(null)} className="text-white text-xl p-1">
                    <FiChevronLeft className={isArabic ? 'rotate-180' : ''} />
                  </button>
                  <h2 className="text-white text-lg font-medium text-center flex-1 mx-4">
                    {isArabic ? selectedPoint.labelAr : selectedPoint.label}
                  </h2>
                  <div className="w-8"></div> {/* Spacer for centering */}
                </div>

                {/* Gallery Content - Horizontal Scrolling */}
                <div className="flex-1 p-4">
                  <motion.div
                    key={`gallery-${selectedPoint.label || selectedPoint.labelAr || 'default'}`}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4 }}
                    className={`flex overflow-x-auto snap-x snap-mandatory scroll-smooth scrollbar-hide h-full ${
                      isArabic ? 'space-x-reverse space-x-4 pl-4' : 'space-x-4 pr-4'
                    }`}
                  >
                    {selectedPoint.gallery.map((src, index) => (
                      <motion.div
                        key={index}
                        className="flex-shrink-0 w-[85%] md:w-80 snap-center group cursor-pointer"
                        custom={index}
                        initial="hidden"
                        animate="show"
                        variants={galleryCardVariants}
                      >
                        <div className="relative w-full h-[60vh] md:h-96 overflow-hidden rounded-lg">
                          <MotionImage
                            src={src}
                            alt={`${isArabic ? selectedPoint.labelAr : selectedPoint.label} ${index + 1}`}
                            fill
                            className="object-cover group-hover:scale-105 transition-transform duration-300"
                            initial={{ scale: 1 }}
                            whileHover={{ scale: 1.05 }}
                            transition={{ duration: 0.3 }}
                          />
                          <div className="absolute bottom-4 left-4 z-10 text-white text-sm bg-black/70 px-3 py-1 rounded">
                            {isArabic ? selectedPoint.labelAr : selectedPoint.label} #{index + 1}
                          </div>
                        </div>

                        {/* Optional: Add description below image */}
                        <div className="mt-3">
                          <p className="text-white text-sm opacity-80">
                            Image {index + 1} of {selectedPoint.gallery.length}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}
